import {
    IFacetGroupFilter,
    IFacetSearchResultTranslations,
} from 'Types/Litium';
import { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { BreakPointHooks } from 'Hooks/BreakPointHooks';
import { Accordion, AccordionChild } from 'Components/Accordion';
import getFiltrationValuesAndUpdateUrlQuery from 'Utils/getFiltrationValuesAndUpdateUrlQuery';
import Overlay from 'Components/Overlay';
import FilterIcon from 'Icons/filter.svg?react';
import CloseIcon from 'Icons/closeThin.svg?react';
import useLockedBody from 'Hooks/useLockedBody';
import CtaButton from 'Components/CtaButton';
import { useIsOverflow } from 'Hooks/useIsOverflow';
import useOutsideClick from 'Hooks/useOutsideClick';
import FilterOption from 'Pages/ProductListingPage/FilterOption';

const { useGreater } = BreakPointHooks();

interface ISlideInFilterProps {
    className?: string;
    facetFilters: IFacetGroupFilter[];
    translations: IFacetSearchResultTranslations;
    getFiltration: (
        param: {
            name: string;
            value: string;
        }[]
    ) => void;
    isFetching: boolean;
}

const SlideInFilter = ({
    className = '',
    facetFilters,
    translations,
    getFiltration,
    isFetching,
}: ISlideInFilterProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const filterRef = useRef<HTMLFormElement>();
    const accordionRef = useRef<HTMLUListElement>();
    const outSideClickRef = useOutsideClick(() =>
        showFilter(false)
    ) as React.LegacyRef<HTMLDivElement>;
    const [, setLocked] = useLockedBody(false);
    const { refYOverflowing, refYScrollBegin, refYScrollEnd } = useIsOverflow(
        accordionRef,
        filterRef
    );
    const [renderOverlay, setRenderOverlay] = useState(false);
    const isGreater = useGreater('lg');
    const totalSelectedOptions = facetFilters?.reduce(
        (totalSelected, filter) =>
            totalSelected + filter.selectedOptions.length,
        0
    );
    const handleFiltration = (
        facetGroups: IFacetGroupFilter[],
        formData: HTMLFormElement,
        shouldReset?: boolean
    ) => {
        const data = new FormData(formData);
        const filtrationValues = getFiltrationValuesAndUpdateUrlQuery(
            facetGroups,
            shouldReset ? new FormData() : data
        );

        getFiltration(filtrationValues);
        if (shouldReset) setIsOpen(false);
    };

    const showFilter = (value: boolean) => {
        setIsOpen(value);
        setLocked(value);
    };

    useEffect(() => {
        if (isGreater) {
            setIsOpen(false);
            setLocked(false);
        }
        if (!renderOverlay) {
            setRenderOverlay(true);
        }
    }, [isGreater]);

    return (
        facetFilters.length > 0 && (
            <div className={`${className}`}>
                <button
                    className="flex items-center gap-2 font-roboto text-sm text-black lg:hidden"
                    type="button"
                    onClick={() => showFilter(true)}
                >
                    <span className="flex items-center gap-1 font-medium">
                        {translations.filterTitleMobile}
                        <span className="text-small flex items-center justify-center rounded-full font-roboto tabular-nums text-black">
                            {totalSelectedOptions}
                        </span>
                    </span>
                    <FilterIcon className="h-5 w-5" />
                </button>
                <div
                    ref={outSideClickRef}
                    className={`fixed bottom-0 left-0 z-50 grid h-2/3 w-full grid-rows-[min-content_1fr_min-content] rounded-t-2xl bg-white shadow-[1px_1px_6px_0px_rgba(0,_0,_0,_0.25)] transition-[transform,_visibility] duration-[400ms] ${
                        isOpen
                            ? 'visible translate-y-0'
                            : 'invisible translate-y-full'
                    }`}
                >
                    <div
                        className={`mt-7 flex justify-between px-4.5 pb-7 transition-shadow duration-layout ${
                            refYOverflowing && !refYScrollBegin
                                ? 'shadow-[0px_10px_5px_-11px_rgba(0,_0,_0,_0.75)]'
                                : 'shadow-none'
                        }`}
                    >
                        <strong className="font-montserrat text-xl font-medium text-black">
                            {translations.filterTitleMobile}
                        </strong>
                        <button
                            className=""
                            type="button"
                            onClick={() => showFilter(false)}
                        >
                            <span className="sr-only">
                                {translations.filterClose}
                            </span>
                            <CloseIcon className="h-3.5 w-3.5 text-black" />
                        </button>
                    </div>
                    <form
                        ref={filterRef}
                        onSubmit={e => e.preventDefault()}
                        className={`-:overflow-auto -:overflow-x-hidden -:px-4.5 ${className}`}
                    >
                        <fieldset>
                            <legend className="sr-only">
                                {translations.filterTitleMobile}
                            </legend>
                            <Accordion ref={accordionRef}>
                                {facetFilters.map(
                                    (filter, index) =>
                                        filter.options?.length > 0 && (
                                            <AccordionChild
                                                key={index}
                                                title={`${filter.label} ${
                                                    filter.selectedOptions
                                                        ?.length
                                                        ? `(${filter.selectedOptions.length})`
                                                        : ''
                                                }`}
                                            >
                                                <fieldset>
                                                    <legend className="sr-only">
                                                        {filter.label}
                                                    </legend>
                                                    <ul
                                                        id={filter.id}
                                                        className={`shadow-semiBold} flex flex-wrap gap-4 p-4.5 ${
                                                            filter.options?.some(
                                                                option =>
                                                                    option.colorHexCode
                                                            )
                                                                ? 'flex-row'
                                                                : 'flex-col'
                                                        }`}
                                                    >
                                                        {filter.options.map(
                                                            (
                                                                option,
                                                                optionIndex
                                                            ) => (
                                                                <FilterOption
                                                                    key={
                                                                        optionIndex
                                                                    }
                                                                    filterIsLoading={
                                                                        isFetching
                                                                    }
                                                                    idPrefix="slidein-filter-group-option"
                                                                    option={
                                                                        option
                                                                    }
                                                                    selected={
                                                                        filter.selectedOptions?.indexOf(
                                                                            option.id
                                                                        ) >= 0
                                                                    }
                                                                    filterGroup={
                                                                        filter.id
                                                                    }
                                                                    toggleCheckbox={() =>
                                                                        handleFiltration(
                                                                            facetFilters,
                                                                            filterRef.current
                                                                        )
                                                                    }
                                                                />
                                                            )
                                                        )}
                                                    </ul>
                                                </fieldset>
                                            </AccordionChild>
                                        )
                                )}
                            </Accordion>
                        </fieldset>
                    </form>
                    <div
                        className={`mb-6 flex gap-1.5 px-4.5 pt-6 transition-shadow duration-layout ${
                            refYOverflowing && !refYScrollEnd
                                ? 'shadow-[0px_-6px_5px_-7px_rgba(0,_0,_0,_0.75)]'
                                : 'shadow-none'
                        }`}
                    >
                        <CtaButton
                            type="button"
                            onClick={() => showFilter(false)}
                        >
                            {translations.filterShowProducts}
                        </CtaButton>
                        <CtaButton
                            type="button"
                            ctaType="secondary"
                            onClick={() => {
                                handleFiltration(
                                    facetFilters,
                                    filterRef.current,
                                    true
                                );
                                showFilter(false);
                            }}
                        >
                            {translations.filterClear}
                        </CtaButton>
                    </div>
                    {renderOverlay && (
                        <>
                            {createPortal(
                                <Overlay isOpen={isOpen} />,
                                document.body,
                                'overlay'
                            )}
                        </>
                    )}
                </div>
            </div>
        )
    );
};

export default SlideInFilter;
